import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CALENDLY_URL } from '../../config';
import clockDashboard from '../../assets/dashboard.png';
const VERTICAL_LOGO = process.env.PUBLIC_URL + '/vertical-logo.svg';

function useDarkMode() {
  const [dark, setDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = e => setDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return dark;
}

const SvgClock = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 7 12 12 15 15"/>
  </svg>
);
const SvgDollar = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);
const SvgCalendar = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const SvgCar = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l3-4h10l3 4h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2"/>
    <circle cx="7.5" cy="17" r="2.5"/><circle cx="16.5" cy="17" r="2.5"/>
  </svg>
);
const SvgUsers = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const SvgDownload = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

function IconBox({ children, dark }) {
  return (
    <div style={{
      width: 56, height: 56, borderRadius: 14,
      background: dark ? 'rgba(34,197,94,0.1)' : '#dcfce7',
      border: `1px solid ${dark ? 'rgba(34,197,94,0.2)' : '#bbf7d0'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 18, flexShrink: 0,
    }}>
      {children}
    </div>
  );
}

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'About',    href: '/about' },
  { label: 'Pricing',  href: '/pricing' },
];

const FEATURES = [
  { Icon: SvgClock,    title: 'Clock In / Clock Out',  desc: 'Employees clock in and out from any device. Hours are captured instantly and flow directly into payroll.' },
  { Icon: SvgDollar,   title: 'Payroll in Minutes',    desc: 'Regular pay, overtime, shift differentials, mileage, and PTO — calculated automatically, ready to export.' },
  { Icon: SvgCalendar, title: 'Schedule to Payroll',   desc: 'Build weekly schedules and push them straight into payroll entries. No double data entry, ever.' },
  { Icon: SvgCar,      title: 'Mileage Reimbursement', desc: 'Set a flat reimbursement rate and mileage is factored into every payroll run automatically.' },
  { Icon: SvgUsers,    title: 'Role-Based Access',     desc: 'Superadmins run payroll, managers submit hours for their teams, employees view their own records.' },
  { Icon: SvgDownload, title: 'ADP-Compatible Export', desc: 'One-click export in ADP format. Upload directly into your existing payroll system — no reformatting.' },
];

const PLANS = [
  { name: 'Starter',      price: '$29/mo',  employees: 'Up to 30 employees',  highlight: false, cta: 'Start free trial' },
  { name: 'Professional', price: '$69/mo',  employees: 'Up to 100 employees', highlight: true,  cta: 'Start free trial' },
  { name: 'Growth',       price: '$149/mo', employees: 'Up to 150 employees', highlight: false, cta: 'Start free trial' },
  { name: 'Enterprise',   price: 'Custom',  employees: 'Unlimited employees',  highlight: false, cta: 'Contact us'       },
];

export default function HomePage() {
  const navigate = useNavigate();
  const dark = useDarkMode();
  const [scrolled,     setScrolled]     = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (href.startsWith('/')) { navigate(href); return; }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const T = dark ? {
    bg:           '#0d1b2a',
    bgCard:       'rgba(255,255,255,0.025)',
    bgCardBorder: 'rgba(34,197,94,0.1)',
    bgNav:        'rgba(10,15,30,0.95)',
    navBorder:    'rgba(34,197,94,0.08)',
    heading:      '#d1fae5',
    text:         '#e2e8f0',
    textMuted:    'rgba(148,163,184,0.75)',
    textFaint:    'rgba(100,116,139,0.6)',
    sectionBorder:'rgba(34,197,94,0.06)',
    navLink:      'rgba(148,163,184,0.85)',
    navLinkHover: '#d1fae5',
    footerText:   'rgba(100,116,139,0.5)',
    statLabel:    'rgba(148,163,184,0.55)',
  } : {
    bg:           '#f8fafc',
    bgCard:       '#ffffff',
    bgCardBorder: '#e2e8f0',
    bgNav:        'rgba(255,255,255,0.97)',
    navBorder:    '#e2e8f0',
    heading:      '#0f172a',
    text:         '#1e293b',
    textMuted:    '#64748b',
    textFaint:    '#94a3b8',
    sectionBorder:'#e2e8f0',
    navLink:      '#475569',
    navLinkHover: '#0f172a',
    footerText:   '#94a3b8',
    statLabel:    '#94a3b8',
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: T.text, overflowX: 'hidden', transition: 'background 0.2s, color 0.2s' }}>
      <style>{`
        * { box-sizing: border-box; }
        .hn { display:flex; justify-content:space-between; align-items:center; padding:16px 40px; position:fixed; top:0; left:0; right:0; z-index:100; backdrop-filter:blur(12px); }
        .hn-links { display:flex; align-items:center; gap:8px; }
        .hn-mob-btn { display:none; background:none; border:none; font-size:24px; cursor:pointer; }
        .hn-mob-menu { display:none; flex-direction:column; gap:10px; padding:16px 24px; position:fixed; top:70px; left:0; right:0; z-index:99; }
        .demo-hero { display:flex; align-items:center; gap:64px; max-width:1200px; margin:0 auto; padding:120px 40px 80px; }
        .demo-hero-text { flex:1; min-width:280px; }
        .demo-hero-img { flex:1; min-width:280px; max-width:580px; }
        .demo-hero-img img { width:100%; border-radius:16px; box-shadow:0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(34,197,94,0.15); }
        .hero { padding:80px 24px 80px; text-align:center; position:relative; overflow:hidden; }
        .hero-h { font-size:clamp(36px,7vw,72px); font-weight:900; letter-spacing:-2px; line-height:1.05; max-width:820px; margin:0 auto 24px; }
        .hero-sub { font-size:clamp(16px,3vw,20px); max-width:560px; margin:0 auto 48px; line-height:1.65; }
        .section { padding:88px 24px; }
        .section-label { font-size:14px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:#22c55e; text-align:center; margin-bottom:14px; }
        .section-title { font-size:clamp(32px,5.5vw,52px); font-weight:900; text-align:center; letter-spacing:-1.5px; line-height:1.1; max-width:700px; margin:0 auto 18px; }
        .section-sub { font-size:17px; text-align:center; max-width:520px; margin:0 auto 60px; line-height:1.65; }
        .grid-3 { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:24px; max-width:1060px; margin:0 auto; }
        .grid-4 { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:20px; max-width:1060px; margin:0 auto; }
        .stats { display:flex; justify-content:center; gap:56px; flex-wrap:wrap; padding:0 24px 80px; }
        .footer { padding:32px 40px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; }
        @media(max-width:768px){
          .hn { padding:14px 20px; }
          .hn-links { display:none; }
          .hn-mob-btn { display:block; }
          .hn-mob-menu { display:flex; }
          .demo-hero { flex-direction:column; padding:100px 20px 48px; gap:36px; }
          .demo-hero-img { max-width:100%; }
          .hero { padding:60px 16px 60px; }
          .section { padding:64px 16px; }
          .stats { gap:32px; }
          .footer { padding:24px 20px; flex-direction:column; text-align:center; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav className="hn" style={{ background: T.bgNav, borderBottom: `1px solid ${T.navBorder}`, boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.1)' : 'none' }}>
        <img src={VERTICAL_LOGO} alt="ClockEarnings" onClick={() => scrollTo('#top')} style={{ height: 38, cursor: 'pointer' }} />
        <div className="hn-links">
          {NAV_LINKS.map(l => (
            <button key={l.label} onClick={() => scrollTo(l.href)}
              style={{ background: 'none', border: 'none', color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 14px', borderRadius: 8 }}
              onMouseEnter={e => e.target.style.color = T.navLinkHover}
              onMouseLeave={e => e.target.style.color = T.navLink}
            >{l.label}</button>
          ))}
          <button onClick={() => setShowCalendly(true)}
            style={{ padding: '8px 16px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, color: '#6366f1', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            📅 Book a Demo
          </button>
          <button onClick={() => navigate('/login')}
            style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.bgCardBorder}`, borderRadius: 8, color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Sign in
          </button>
          <button onClick={() => navigate('/register')}
            style={{ padding: '9px 18px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Start free trial
          </button>
        </div>
        <button className="hn-mob-btn" style={{ color: T.navLink }} onClick={() => setMobileOpen(o => !o)}>{mobileOpen ? '✕' : '☰'}</button>
      </nav>

      {/* ── Mobile menu ── */}
      {mobileOpen && (
        <div className="hn-mob-menu" style={{ background: T.bgNav, borderBottom: `1px solid ${T.navBorder}` }}>
          {NAV_LINKS.map(l => (
            <button key={l.label} onClick={() => scrollTo(l.href)}
              style={{ background: 'none', border: 'none', color: T.navLink, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left', padding: '8px 0' }}>
              {l.label}
            </button>
          ))}
          <button onClick={() => { setShowCalendly(true); setMobileOpen(false); }}
            style={{ padding: '10px 16px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, color: '#6366f1', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            📅 Book a Demo
          </button>
          <button onClick={() => navigate('/login')}
            style={{ padding: '10px 16px', background: 'transparent', border: `1px solid ${T.bgCardBorder}`, borderRadius: 8, color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Sign in
          </button>
          <button onClick={() => navigate('/register')}
            style={{ padding: '10px 16px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Start free trial
          </button>
        </div>
      )}

      {/* ── Original Hero ── */}
      <section id="top" className="hero" style={{ borderBottom: `1px solid ${T.sectionBorder}` }}>
        {dark && <>
          <div style={{ position: 'absolute', top: 0, left: '25%', width: 600, height: 500, background: 'radial-gradient(ellipse,rgba(34,197,94,0.1) 0%,transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: 120, right: '15%', width: 400, height: 350, background: 'radial-gradient(ellipse,rgba(139,92,246,0.07) 0%,transparent 70%)', pointerEvents: 'none' }} />
        </>}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 18px', background: dark ? 'rgba(34,197,94,0.08)' : '#dcfce7', border: `1px solid ${dark ? 'rgba(34,197,94,0.2)' : '#bbf7d0'}`, borderRadius: 100, fontSize: 13, color: dark ? '#93c5fd' : '#15803d', fontWeight: 700, letterSpacing: '.04em', marginBottom: 28 }}>
          Purpose-built for home health agencies
        </div>
        <h1 className="hero-h" style={{ color: T.heading }}>
         From Schedule to payroll<br />
          <span style={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
           A tool that work around your needs. 
          </span>
        </h1>
        <p className="hero-sub" style={{ color: T.textMuted, fontWeight: 500 }}>
          ClockEarnings handles the complexity of home health payroll — split shifts, live-in rates, mileage, overtime, and ADP export — all in one place.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          <button onClick={() => navigate('/register')}
            style={{ padding: '15px 32px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(34,197,94,0.35)' }}>
            Start your free 14-day trial
          </button>
          <button onClick={() => setShowCalendly(true)}
            style={{ padding: '15px 32px', background: dark ? 'rgba(99,102,241,0.1)' : '#eef2ff', border: `1px solid ${dark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`, borderRadius: 12, color: '#6366f1', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
            📅 Book a Demo
          </button>
        </div>
        <p style={{ fontSize: 13, color: T.textFaint, fontWeight: 500 }}>14-day free trial · No credit card required · Cancel anytime</p>
      </section>

      {/* ── Stats ── */}
      <div className="stats">
        {[
          { value: '14 days', label: 'Free trial'    },
          { value: '$29/mo',  label: 'Starting price' },
          { value: '< 5 min', label: 'Setup time'     },
          { value: '100%',    label: 'Web-based'      },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'clamp(28px,4vw,40px)', fontWeight: 900, color: '#22c55e', letterSpacing: '-1.5px', lineHeight: 1, marginBottom: 6 }}>{s.value}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.statLabel, letterSpacing: '.08em', textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Features ── */}
      <section id="features" className="section" style={{ borderTop: `1px solid ${T.sectionBorder}`, borderBottom: `1px solid ${T.sectionBorder}`, background: dark ? 'rgba(10,46,26,0.3)' : '#f0fdf4' }}>
        <div className="section-label">Features</div>
        <h2 className="section-title" style={{ color: T.heading }}>Built around how payroll actually works</h2>
        <p className="section-sub" style={{ color: T.textMuted, fontWeight: 500 }}>Every feature maps to a real step in your payroll process — from clocking in to final export.</p>
        <div className="grid-3">
          {FEATURES.map(f => (
            <div key={f.title} style={{ background: T.bgCard, border: '2px solid #22c55e', borderRadius: 18, padding: '32px 28px', boxShadow: dark ? '0 0 16px rgba(34,197,94,0.08)' : '0 2px 12px rgba(34,197,94,0.12)' }}>
              <IconBox dark={dark}><f.Icon /></IconBox>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.heading, marginBottom: 10 }}>{f.title}</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: T.textMuted, lineHeight: 1.7 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="section" style={{ borderTop: `1px solid ${T.sectionBorder}` }}>
        <div className="section-label">How it works</div>
        <h2 className="section-title" style={{ color: T.heading }}>From setup to first payroll run</h2>
        <p className="section-sub" style={{ color: T.textMuted, fontWeight: 500 }}>No IT department needed. Most teams are running payroll the same day they sign up.</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 0, flexWrap: 'wrap', maxWidth: 900, margin: '0 auto' }}>
          {[
            { n: 1, title: 'Register your agency',  desc: 'Add your business details, pay periods, overtime rules, and lunch policies in minutes.' },
            { n: 2, title: 'Invite your team',       desc: 'Send email invites with role-based access. Managers submit hours, employees view their own records.' },
            { n: 3, title: 'Run payroll in minutes', desc: 'Log hours, review calculated pay, and export to ADP — all from one dashboard.' },
          ].map(s => (
            <div key={s.n} style={{ flex: 1, minWidth: 200, textAlign: 'center', padding: '0 24px 32px' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff', fontSize: 22, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 0 24px rgba(34,197,94,0.3)' }}>{s.n}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.heading, marginBottom: 10 }}>{s.title}</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: T.textMuted, lineHeight: 1.7 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Book a Demo (image left, text right) ── */}
      <div style={{ background: dark ? 'linear-gradient(160deg, rgba(15,77,46,0.18) 0%, rgba(10,15,30,0) 60%)' : 'linear-gradient(160deg, #f0fdf4 0%, #f8fafc 60%)', borderTop: `1px solid ${T.sectionBorder}`, borderBottom: `1px solid ${T.sectionBorder}` }}>
        <div className="demo-hero">
          <div className="demo-hero-img">
            <img src={clockDashboard} alt="ClockEarnings Dashboard Preview" />
          </div>
          <div className="demo-hero-text">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 100, fontSize: 12, color: '#16a34a', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 24 }}>
              📅 Live Demo Available
            </div>
            <h2 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, color: T.heading, letterSpacing: '-1.5px', lineHeight: 1.08, margin: '0 0 20px' }}>
              See ClockEarnings<br />
              <span style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                in action
              </span>
            </h2>
            <p style={{ fontSize: 16, color: T.textMuted, lineHeight: 1.7, margin: '0 0 28px', fontWeight: 500 }}>
              Book a free 30-minute demo and see how ClockEarnings simplifies payroll for your team — from time entry to final export.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
              {[
                { icon: '🗓️', text: 'Pick a time that works for you' },
                { icon: '🎯', text: 'Live walkthrough, not a recording' },
                { icon: '💬', text: 'Ask questions and get real answers' },
              ].map(p => (
                <div key={p.text} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 600, color: T.textMuted }}>
                  <span style={{ fontSize: 20 }}>{p.icon}</span> {p.text}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => setShowCalendly(true)}
                style={{ padding: '14px 28px', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 24px rgba(99,102,241,0.3)' }}>
                📅 Book a Free Demo
              </button>
              <button onClick={() => navigate('/register')}
                style={{ padding: '14px 28px', background: dark ? 'rgba(255,255,255,0.04)' : '#f1f5f9', border: `1px solid ${T.bgCardBorder}`, borderRadius: 10, color: T.navLink, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
                Start free trial →
              </button>
            </div>
            <p style={{ fontSize: 13, color: T.textFaint, marginTop: 14, fontWeight: 500 }}>
              No sales pressure · 20 minutes · Free
            </p>
          </div>
        </div>
      </div>

      {/* ── Pricing ── */}
      <section className="section" style={{ borderTop: `1px solid ${T.sectionBorder}` }}>
        <div className="section-label">Pricing</div>
        <h2 className="section-title" style={{ color: T.heading }}>Simple, transparent pricing</h2>
        <p className="section-sub" style={{ color: T.textMuted, fontWeight: 500 }}>Flat monthly rate — no per-seat fees, no surprise charges.</p>
        <div className="grid-4" style={{ marginBottom: 32 }}>
          {PLANS.map(p => (
            <div key={p.name} style={{
              background: p.highlight ? (dark ? 'rgba(99,102,241,0.1)' : '#eef2ff') : T.bgCard,
              border: `1px solid ${p.highlight ? 'rgba(99,102,241,0.4)' : T.bgCardBorder}`,
              borderRadius: 18, padding: '28px 24px', position: 'relative',
              boxShadow: dark ? 'none' : '0 1px 4px rgba(0,0,0,0.05)',
            }}>
              {p.highlight && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', padding: '3px 16px', borderRadius: 99, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ fontSize: 15, fontWeight: 800, color: T.heading, marginBottom: 4 }}>{p.name}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: p.highlight ? '#6366f1' : '#22c55e', marginBottom: 2 }}>{p.price}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: T.textFaint, marginBottom: 20 }}>{p.employees}</div>
              <button
                onClick={() => p.name === 'Enterprise' ? window.location.href = 'mailto:contact@flurrysystems.com' : navigate('/register')}
                style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer', background: p.highlight ? 'linear-gradient(135deg,#4f46e5,#7c3aed)' : (dark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'), color: p.highlight ? 'white' : T.navLink, fontWeight: 700, fontSize: 13 }}
              >{p.cta}</button>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <button onClick={() => navigate('/pricing')}
            style={{ background: 'none', border: `1px solid ${dark ? 'rgba(34,197,94,0.2)' : '#bbf7d0'}`, borderRadius: 8, color: '#16a34a', padding: '10px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            See full pricing details →
          </button>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section" style={{ textAlign: 'center', position: 'relative', borderTop: `1px solid ${T.sectionBorder}` }}>
        {dark && <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 700, height: 300, background: 'radial-gradient(ellipse,rgba(34,197,94,0.07) 0%,transparent 70%)', pointerEvents: 'none' }} />}
        <h2 style={{ fontSize: 'clamp(30px,5vw,48px)', fontWeight: 900, color: T.heading, letterSpacing: '-1.5px', maxWidth: 640, margin: '0 auto 20px' }}>
          Ready to simplify payroll?
        </h2>
        <p style={{ fontSize: 17, fontWeight: 500, color: T.textMuted, maxWidth: 440, margin: '0 auto 36px', lineHeight: 1.65 }}>
          Start your 14-day free trial — no credit card required. You'll be running payroll the same day.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/register')}
            style={{ padding: '15px 32px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(34,197,94,0.35)' }}>
            Get started free
          </button>
          <button onClick={() => setShowCalendly(true)}
            style={{ padding: '15px 32px', background: dark ? 'rgba(255,255,255,0.04)' : '#f1f5f9', border: `1px solid ${T.bgCardBorder}`, borderRadius: 12, color: T.navLink, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
            📅 Book a Demo
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer" style={{ borderTop: `1px solid ${T.sectionBorder}` }}>
        <img src={VERTICAL_LOGO} alt="ClockEarnings" style={{ height: 30 }} />
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {NAV_LINKS.map(l => (
            <button key={l.label} onClick={() => scrollTo(l.href)}
              style={{ background: 'none', border: 'none', color: T.footerText, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              {l.label}
            </button>
          ))}
          <a href="mailto:contact@flurrysystems.com" style={{ color: T.footerText, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Contact</a>
          <button onClick={() => navigate('/privacy')}
            style={{ background: 'none', border: 'none', color: T.footerText, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            Privacy Policy
          </button>
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.footerText }}>© {new Date().getFullYear()} ClockEarnings by Flurry Systems</span>
      </footer>

      {/* ── Calendly modal ── */}
      {showCalendly && (
        <div onClick={() => setShowCalendly(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, height: '90vh', background: '#fff', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 800, fontSize: '1rem', color: '#111827' }}>📅 Book a Demo</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>Pick a time and we'll walk you through ClockEarnings live.</p>
              </div>
              <button onClick={() => setShowCalendly(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
            </div>
            <iframe src={`${CALENDLY_URL}?embed_type=Inline&hide_gdpr_banner=1`} title="Book a Demo" style={{ flex: 1, border: 'none', width: '100%' }} />
          </div>
        </div>
      )}

    </div>
  );
}
